import { useState } from "react";
import { Crown, ArrowLeft, ArrowRight, ChevronRight, ChevronDown } from "lucide-react";

/* ─── flatten depth-first (L before R), tracking parentId ─────── */
function flatten(node, depth = 0, isLastArr = [], parentId = null, rows = []) {
    if (!node) return rows;
    rows.push({ ...node, depth, isLastArr: [...isLastArr], parentId, children: undefined });
    const kids  = node.children || [];
    const left  = kids.filter(c => c.side === "L");
    const right = kids.filter(c => c.side === "R");
    const all   = [...left, ...right];
    all.forEach((c, i) =>
        flatten(c, depth + 1, [...isLastArr, i === all.length - 1], node.id, rows)
    );
    return rows;
}

/* ─── Avatar ────────────────────────────────────────────────────── */
function Avatar({ name, depth, side }) {
    const letters = name.split(" ").map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
    const cls = depth === 0
        ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md"
        : side === "L"
        ? "bg-teal-50 text-teal-700 ring-1 ring-teal-200"
        : "bg-rose-50 text-rose-600 ring-1 ring-rose-200";
    return (
        <div className={`w-10 h-10 rounded-xl flex-shrink-0 grid place-items-center text-xs font-bold ${cls}`}>
            {letters}
        </div>
    );
}

/* ─── Wing chip ─────────────────────────────────────────────────── */
function WingChip({ isRoot, side }) {
    if (isRoot) return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold
            bg-[#0C2B5C] text-white whitespace-nowrap">
            <Crown className="w-3 h-3" /> SOVEREIGN
        </span>
    );
    if (side === "L") return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold
            bg-teal-50 text-teal-700 border border-teal-200 whitespace-nowrap">
            <ArrowLeft className="w-3 h-3" /> LEFT
        </span>
    );
    return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold
            bg-rose-50 text-rose-600 border border-rose-200 whitespace-nowrap">
            <ArrowRight className="w-3 h-3" /> RIGHT
        </span>
    );
}

/* ─── Level badge ───────────────────────────────────────────────── */
function LevelBadge({ depth }) {
    const label = depth === 0 ? "ROOT" : `L${depth}`;
    const styles = [
        "bg-[#0C2B5C] text-white",
        "bg-[#2172E5] text-white",
        "bg-blue-400 text-white",
        "bg-blue-200 text-blue-800",
        "bg-slate-100 text-slate-600",
    ];
    return (
        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap
            ${styles[Math.min(depth, styles.length - 1)]}`}>
            {label}
        </span>
    );
}

/* ─── Network balance bar (single bar, teal left / rose right) ─── */
function NetworkBar({ left, right }) {
    const total = left + right;
    const lPct  = total > 0 ? (left  / total) * 100 : 0;
    const rPct  = total > 0 ? (right / total) * 100 : 0;
    return (
        <div className="min-w-[160px]">
            <div className="flex justify-between text-[10px] font-bold mb-1.5">
                <span className="text-teal-600">{left} Left</span>
                <span className="text-rose-500">{right} Right</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                {total > 0 ? (
                    <>
                        <div className="h-full bg-teal-500 transition-all duration-300 rounded-l-full"
                            style={{ width: `${lPct}%` }} />
                        <div className="h-full bg-rose-500 transition-all duration-300 rounded-r-full"
                            style={{ width: `${rPct}%` }} />
                    </>
                ) : (
                    <div className="h-full w-full bg-slate-200 rounded-full" />
                )}
            </div>
        </div>
    );
}

/* ─── Tree connector prefix ─────────────────────────────────────── */
function TreePrefix({ depth, isLastArr }) {
    if (depth === 0) return null;
    const parts = [];
    for (let i = 0; i < depth - 1; i++) {
        parts.push(
            <span key={i} className="inline-flex w-4 justify-center text-slate-200 font-mono text-sm select-none">
                {isLastArr[i] ? " " : "│"}
            </span>
        );
    }
    parts.push(
        <span key="conn" className="inline-flex w-5 items-center text-slate-300 font-mono text-sm select-none">
            {isLastArr[depth - 1] ? "└─" : "├─"}
        </span>
    );
    return <span className="shrink-0">{parts}</span>;
}

/* ─── Main component ─────────────────────────────────────────────── */
export default function TreeTable({ root, wingFilter = "ALL", search = "" }) {
    const [collapsed, setCollapsed] = useState(new Set());

    if (!root) return null;

    const rows      = flatten(root);
    const rowMap    = new Map(rows.map(r => [r.id, r]));
    const parentIds = new Set(rows.map(r => r.parentId).filter(Boolean));
    const hasKids   = id => parentIds.has(id);

    const toggle = id =>
        setCollapsed(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const isFiltering = wingFilter !== "ALL" || search.trim() !== "";

    /* Collapse filtering: skip nodes whose ancestor is collapsed */
    const baseRows = isFiltering
        ? rows
        : rows.filter(row => {
            let pid = row.parentId;
            while (pid) {
                if (collapsed.has(pid)) return false;
                pid = rowMap.get(pid)?.parentId;
            }
            return true;
        });

    /* Wing + search filter */
    const visibleRows = baseRows.filter(row => {
        if (wingFilter !== "ALL") {
            if (wingFilter === "SOVEREIGN" && row.depth !== 0) return false;
            if (wingFilter === "DIRECT"    && row.depth !== 1) return false;
            if (wingFilter === "L"         && row.side  !== "L") return false;
            if (wingFilter === "R"         && row.side  !== "R") return false;
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            return row.full_name.toLowerCase().includes(q) ||
                   (row.email || "").toLowerCase().includes(q);
        }
        return true;
    });

    const activeCount = visibleRows.filter(r => r.status === "active").length;
    const lTotal      = rows.filter(r => r.side === "L").length;
    const rTotal      = rows.filter(r => r.side === "R").length;
    const maxDepth    = Math.max(...rows.map(r => r.depth));

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">

                    {/* ── Head ── */}
                    <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/60">
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase w-20 text-center">
                                #
                            </th>
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase text-left">
                                Member Details
                            </th>
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase text-left">
                                Wing
                            </th>
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase text-left">
                                Level
                            </th>
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase text-left">
                                Network Balance Structure (Left vs Right)
                            </th>
                            <th className="py-4 px-4 text-[11px] font-bold tracking-wider text-slate-400 uppercase text-right">
                                Status
                            </th>
                        </tr>
                    </thead>

                    {/* ── Body ── */}
                    <tbody className="divide-y divide-slate-50">
                        {visibleRows.map((row, i) => {
                            const isRoot      = row.depth === 0;
                            const isActive    = row.status === "active";
                            const isCollapsed = collapsed.has(row.id);
                            const rowHasKids  = hasKids(row.id);
                            const evenBg      = i % 2 === 0 ? "bg-white" : "bg-slate-50/40";

                            return (
                                <tr key={row.id}
                                    className={`group transition-colors duration-100 ${evenBg} hover:bg-blue-50/30`}>

                                    {/* # + expand chevron */}
                                    <td className="py-3.5 px-4 text-center w-20">
                                        <div className="flex items-center justify-center gap-1">
                                            {rowHasKids && !isFiltering ? (
                                                <button
                                                    onClick={() => toggle(row.id)}
                                                    className="text-slate-400 hover:text-[#2172E5] transition-colors">
                                                    {isCollapsed
                                                        ? <ChevronRight className="w-3.5 h-3.5" />
                                                        : <ChevronDown  className="w-3.5 h-3.5" />}
                                                </button>
                                            ) : (
                                                <span className="w-3.5 h-3.5 inline-block" />
                                            )}
                                            <span className="text-slate-300 text-xs font-mono font-semibold w-5 text-center">
                                                {i === 0 ? "—" : i}
                                            </span>
                                        </div>
                                    </td>

                                    {/* Member details */}
                                    <td className="py-3.5 px-4">
                                        <div className="flex items-center gap-3">
                                            <Avatar name={row.full_name} depth={row.depth} side={row.side} />
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {!isFiltering && (
                                                        <TreePrefix depth={row.depth} isLastArr={row.isLastArr} />
                                                    )}
                                                    <span className={`font-bold text-sm truncate
                                                        ${isRoot ? "text-[#0C2B5C]" : "text-slate-800"}`}>
                                                        {row.full_name}
                                                    </span>
                                                    {isRoot && (
                                                        <>
                                                            <span className="text-[10px] font-bold text-[#2172E5]
                                                                bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100">
                                                                You
                                                            </span>
                                                            <span className="text-[10px] font-bold text-slate-500
                                                                bg-slate-100 px-1.5 py-0.5 rounded-full">
                                                                Root Node
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                    {row.referral_code && (
                                                        <span className="font-mono text-[11px] font-bold text-[#2172E5] bg-blue-50
                                                            px-1.5 py-0.5 rounded border border-blue-100 shrink-0">
                                                            {row.referral_code}
                                                        </span>
                                                    )}
                                                    {row.email && (
                                                        <span className="text-[11px] text-slate-400 truncate max-w-[180px]">
                                                            {row.email}
                                                        </span>
                                                    )}
                                                </div>
                                                {row.activated_at && (
                                                    <div className="text-[10px] text-slate-300 mt-0.5">
                                                        Joined {new Date(row.activated_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* Wing */}
                                    <td className="py-3.5 px-4">
                                        <WingChip isRoot={isRoot} side={row.side} />
                                    </td>

                                    {/* Level */}
                                    <td className="py-3.5 px-4">
                                        <LevelBadge depth={row.depth} />
                                    </td>

                                    {/* Network bar */}
                                    <td className="py-3.5 px-4">
                                        <NetworkBar
                                            left={row.left_active_count  || 0}
                                            right={row.right_active_count || 0}
                                        />
                                    </td>

                                    {/* Status */}
                                    <td className="py-3.5 px-4 text-right">
                                        {isActive ? (
                                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                                ACTIVE
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">
                                                <span className="w-2 h-2 rounded-full bg-slate-300" />
                                                {(row.status || "").toUpperCase()}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}

                        {visibleRows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="py-16 text-center text-sm text-slate-400">
                                    No members match the current filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Footer ── */}
            <div className="bg-slate-50/50 border-t border-slate-100 px-6 py-4
                flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs text-slate-500">
                    Showing{" "}
                    <span className="font-bold text-slate-700">{visibleRows.length}</span>
                    {" "}of{" "}
                    <span className="font-bold text-slate-700">{rows.length}</span>
                    {" "}network nodes in direct hierarchy{" "}·{" "}
                    <span className="text-emerald-600 font-semibold">{activeCount} active</span>
                </p>
                <div className="flex items-center gap-3 text-[11px] font-semibold">
                    <span className="flex items-center gap-1.5 text-teal-600">
                        <span className="w-2 h-2 rounded-full bg-teal-500" /> {lTotal} Left
                    </span>
                    <span className="text-slate-200">·</span>
                    <span className="flex items-center gap-1.5 text-rose-500">
                        <span className="w-2 h-2 rounded-full bg-rose-400" /> {rTotal} Right
                    </span>
                    <span className="text-slate-200">·</span>
                    <span className="text-slate-400 font-medium">Max depth: L{maxDepth}</span>
                </div>
            </div>
        </div>
    );
}
